/**
 * CNC PM PWA — In-App & PWA Due Reminder Engine (reminderEngine.ts)
 * 
 * Manages maintenance due reminders, PWA web notification dispatch,
 * and operator alerts when machines are Due Soon or Overdue.
 */

import type { Machine, MachineStatus } from '../../types';
import { evaluateMachine, type MachineScheduleResult, type PMStatus } from './statusCalculator';

export interface MachineReminder {
  id: string;
  machineId: string;
  machineName: string;
  machineModel: string;
  status: PMStatus;
  urgency: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  reasons: string[];
  cumulativeHours: number;
  lastInspectionDate?: string;
  timestamp: string;
}

export interface ReminderSummary {
  reminders: MachineReminder[];
  overdueCount: number;
  dueSoonCount: number;
  normalCount: number;
  totalAttentionNeeded: number;
  highestUrgency: 'critical' | 'warning' | 'normal';
}

const DISMISSED_SESSION_KEY = 'cnc_pm_dismissed_reminders';

/**
 * Scan a list of machines and return all active maintenance reminders
 */
export function generateFleetReminders(
  machines: Machine[],
  now: Date = new Date()
): ReminderSummary {
  const reminders: MachineReminder[] = [];
  let overdueCount = 0;
  let dueSoonCount = 0;
  let normalCount = 0;

  for (const machine of machines) {
    const evalResult = evaluateMachine(machine, undefined, now);
    const status = evalResult.overallStatus;

    if (status === 'OVERDUE') {
      overdueCount++;
      reminders.push({
        id: `alert_${machine.id}_${status}`,
        machineId: machine.id,
        machineName: machine.name,
        machineModel: machine.model,
        status: 'OVERDUE',
        urgency: 'critical',
        title: `🚨 เกินกำหนดบำรุงรักษา (OVERDUE): ${machine.name}`,
        message: evalResult.worstCaseReason,
        reasons: evalResult.reasons,
        cumulativeHours: machine.cumulativeHours,
        lastInspectionDate: machine.lastInspectionDate,
        timestamp: new Date().toISOString(),
      });
    } else if (status === 'DUE_SOON') {
      dueSoonCount++;
      reminders.push({
        id: `alert_${machine.id}_${status}`,
        machineId: machine.id,
        machineName: machine.name,
        machineModel: machine.model,
        status: 'DUE_SOON',
        urgency: 'warning',
        title: `⚠️ ใกล้ถึงกำหนดบำรุงรักษา (DUE SOON): ${machine.name}`,
        message: evalResult.worstCaseReason,
        reasons: evalResult.reasons,
        cumulativeHours: machine.cumulativeHours,
        lastInspectionDate: machine.lastInspectionDate,
        timestamp: new Date().toISOString(),
      });
    } else {
      normalCount++;
    }
  }

  // Sort reminders: OVERDUE first, then DUE_SOON
  reminders.sort((a, b) => {
    if (a.urgency === 'critical' && b.urgency !== 'critical') return -1;
    if (b.urgency === 'critical' && a.urgency !== 'critical') return 1;
    return a.machineName.localeCompare(b.machineName);
  });

  const highestUrgency =
    overdueCount > 0 ? 'critical' : dueSoonCount > 0 ? 'warning' : 'normal';

  return {
    reminders,
    overdueCount,
    dueSoonCount,
    normalCount,
    totalAttentionNeeded: overdueCount + dueSoonCount,
    highestUrgency,
  };
}

/**
 * Check if the browser environment supports Web Notifications
 */
export function isNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * Retrieve the current notification permission state
 */
export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Request notification permission from the user/operator
 */
export async function requestNotificationPermission(): Promise<
  NotificationPermission | 'unsupported'
> {
  if (!isNotificationSupported()) return 'unsupported';
  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (err) {
    console.error('[ReminderEngine] Error requesting notification permission:', err);
    return 'denied';
  }
}

/**
 * Dispatch a native PWA / browser web notification for a machine reminder
 */
export async function dispatchWebNotification(reminder: MachineReminder): Promise<boolean> {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return false;
  }

  const title = reminder.title;
  const options: NotificationOptions & { renotify?: boolean } = {
    body: `${reminder.machineId} (${reminder.machineModel}): ${reminder.message}`,
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: `pm-alert-${reminder.machineId}`,
    renotify: true,
    data: {
      machineId: reminder.machineId,
      url: `/#/inspect/${reminder.machineId}`,
    },
  };

  try {
    // Attempt Service Worker showNotification first (PWA compliant)
    if ('serviceWorker' in navigator && navigator.serviceWorker.ready) {
      try {
        const reg = await navigator.serviceWorker.ready;
        if (reg && reg.showNotification) {
          await reg.showNotification(title, options);
          return true;
        }
      } catch (swErr) {
        // Fall back to standard Notification
      }
    }

    // Direct Notification fallback
    new Notification(title, options);
    return true;
  } catch (err) {
    console.warn('[ReminderEngine] Web Notification dispatch failed:', err);
    return false;
  }
}

/**
 * Store dismissed reminder ID in sessionStorage
 */
export function dismissReminder(machineId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(DISMISSED_SESSION_KEY);
    const set = new Set<string>(raw ? JSON.parse(raw) : []);
    set.add(machineId);
    sessionStorage.setItem(DISMISSED_SESSION_KEY, JSON.stringify(Array.from(set)));
  } catch (e) {
    // Ignore storage quota errors
  }
}

/**
 * Check whether a machine reminder has been temporarily dismissed this session
 */
export function isReminderDismissed(machineId: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = sessionStorage.getItem(DISMISSED_SESSION_KEY);
    if (!raw) return false;
    const set = new Set<string>(JSON.parse(raw));
    return set.has(machineId);
  } catch (e) {
    return false;
  }
}

/**
 * Clear all session-dismissed reminders
 */
export function clearDismissedReminders(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(DISMISSED_SESSION_KEY);
  } catch (e) {
    // Ignore
  }
}
